import React, { useState, useEffect, useMemo } from 'react';
import { tradingService } from '../../../services/tradingService';
import PlotlyChart from '../../../components/charts/PlotlyChart';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const HEATMAP_SCALE = [[0, '#dc2626'], [0.5, '#ffffff'], [1, '#22c55e']];

const fmtPct = (v) => (v != null ? (v * 100).toFixed(1) + '%' : '');

const CalendarPage = ({ analyticsParams, paramsKey }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedStrategy, setSelectedStrategy] = useState('');

  useEffect(() => {
    if (!analyticsParams) return;
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      try {
        const fetchFn = () => tradingService.analyticsCalendar(analyticsParams);
        const res = await fetchFn();
        if (!cancelled) {
          setData(res.data);
          const monthlyKeys = Object.keys(res.data.monthly || {});
          if (monthlyKeys.length > 0 && !selectedStrategy) {
            setSelectedStrategy(monthlyKeys[0]);
          }
        }
      } catch {
        if (!cancelled) setData(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchData();
    return () => { cancelled = true; };
  }, [paramsKey]);

  // Annual heatmap (years x strategies)
  const annualHeatmap = useMemo(() => {
    if (!data?.yearly) return null;
    const { index: rawYears, columns: strategies, data: rows } = data.yearly;
    const years = rawYears.map((d) => {
      const v = typeof d === 'string' ? d.substring(0, 4) : String(d);
      return v;
    });

    const annotations = [];
    for (let i = 0; i < years.length; i++) {
      for (let j = 0; j < strategies.length; j++) {
        const val = rows[i]?.[j];
        annotations.push({
          x: strategies[j], y: years[i],
          text: fmtPct(val),
          showarrow: false,
          font: { size: 10, color: val != null && Math.abs(val) > 0.15 ? '#fff' : '#333' },
        });
      }
    }

    return {
      trace: {
        z: rows, x: strategies, y: years,
        type: 'heatmap', colorscale: HEATMAP_SCALE,
        zmid: 0, zmin: -0.3, zmax: 0.3,
        hovertemplate: '%{y} - %{x}: %{z:.2%}<extra></extra>',
        colorbar: { tickformat: '.0%', title: 'Return' },
      },
      annotations,
      height: Math.max(350, years.length * 30 + 100),
    };
  }, [data]);

  // Monthly heatmap for selected strategy (years x months)
  const monthlyHeatmap = useMemo(() => {
    if (!data?.monthly || !selectedStrategy || !data.monthly[selectedStrategy]) return null;
    const mdf = data.monthly[selectedStrategy];
    const { index: rawYears, columns: rawMonths, data: rows } = mdf;
    const years = rawYears.map((d) => typeof d === 'string' ? d.substring(0, 4) : String(d));
    const months = rawMonths.map((c, i) => MONTHS[i] || c);

    const annotations = [];
    for (let i = 0; i < years.length; i++) {
      for (let j = 0; j < months.length; j++) {
        const val = rows[i]?.[j];
        annotations.push({
          x: months[j], y: years[i],
          text: fmtPct(val),
          showarrow: false,
          font: { size: 10, color: val != null && Math.abs(val) > 0.05 ? '#fff' : '#333' },
        });
      }
    }

    return {
      trace: {
        z: rows, x: months, y: years,
        type: 'heatmap', colorscale: HEATMAP_SCALE,
        zmid: 0, zmin: -0.1, zmax: 0.1,
        hovertemplate: '%{y} %{x}: %{z:.2%}<extra></extra>',
        colorbar: { tickformat: '.0%', title: 'Return' },
      },
      annotations,
      height: Math.max(400, years.length * 35 + 120),
    };
  }, [data, selectedStrategy]);

  const selectCls = 'bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-gray-700 rounded-lg px-3 py-1.5 text-sm text-gray-800 dark:text-gray-200 focus:ring-2 focus:ring-brand-gold/30 focus:border-brand-gold outline-none';

  if (loading) return <div className="text-center py-12 text-gray-500">Loading...</div>;
  if (!data) return <div className="text-center py-12 text-gray-500">No data -- run some backtests first</div>;

  const monthlyStrategies = Object.keys(data.monthly || {});

  return (
    <div>
      {/* Annual Returns Heatmap */}
      {annualHeatmap && (
        <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl p-4 md:p-5 mb-4">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Annual Returns Heatmap</h3>
          <div className="overflow-x-auto">
            <PlotlyChart
              data={[annualHeatmap.trace]}
              interactive
              layout={{
                paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                font: { color: '#9ca3af', size: 10 },
                xaxis: { side: 'top', tickangle: -45 },
                yaxis: { autorange: 'reversed' },
                annotations: annualHeatmap.annotations,
                margin: { l: 60, r: 15, t: 80, b: 20 },
                hovermode: 'closest',
              }}
              style={{ minHeight: annualHeatmap.height }}
            />
          </div>
        </div>
      )}

      {/* Monthly Heatmap with Strategy Selector */}
      <div className="bg-white dark:bg-[#161b22] border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden mb-4">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3 border-b border-gray-200 dark:border-gray-800">
          <span className="font-semibold text-sm text-gray-800 dark:text-gray-200">Monthly Returns Heatmap</span>
          <select
            className={`${selectCls} max-w-xs`}
            value={selectedStrategy}
            onChange={(e) => setSelectedStrategy(e.target.value)}
          >
            {monthlyStrategies.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div className="p-4 md:p-5">
          {monthlyHeatmap ? (
            <div className="overflow-x-auto">
              <PlotlyChart
                data={[monthlyHeatmap.trace]}
                interactive
                layout={{
                  paper_bgcolor: 'transparent', plot_bgcolor: 'transparent',
                  font: { color: '#9ca3af', size: 10 },
                  title: `${selectedStrategy} - Monthly Returns`,
                  xaxis: { side: 'top' },
                  yaxis: { autorange: 'reversed' },
                  annotations: monthlyHeatmap.annotations,
                  margin: { l: 60, r: 15, t: 80, b: 20 },
                  hovermode: 'closest',
                }}
                style={{ minHeight: monthlyHeatmap.height }}
              />
            </div>
          ) : (
            <p className="text-center py-8 text-sm text-gray-500 dark:text-gray-400">
              Select a strategy to view monthly returns.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default CalendarPage;
